import React from 'react';

import { connect } from 'react-redux';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import cx from 'classnames';

import { stripe as stripePromise } from '../../../../App';
import { RootState } from '../../../../slices/rootReducer';
import {
  selectConfirmingPaymentProState,
  selectUpgradeAccountError,
  selectUpgradeToProClientSecret,
  selectUpgradingToProState
} from '../../../../slices/upgradeAccount/upgradeToPro.selectors';
import { CreditCardForm } from './upgradeCreditCardForm';
import { CadenceTypesValues, pricePerCadence } from '../../../auth/proRegister/subscriptionTypes';
import { getPromotionalDiscount } from '../../../../slices/upgradeAccount/promotionalCode/selectors';
import { BlockedByFeature } from '../../../../featureToggles/features/utils';
import { IPromotionalCodeDiscount } from '../../../../slices/upgradeAccount/promotionalCode/interfaces';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';
import { FeatureToggle, Off, On } from '../../../../featureToggles';
import { getDiscountedPrice, getPriceByCadence } from './utils';
import { selectSubscriptionPrices } from '../../../../slices/upgradeAccount/subscriptionPrices/selectors';
import { ISubscriptionPrices } from '../../../../slices/upgradeAccount/subscriptionPrices/interfaces';
import { actions } from '../../../../slices/upgradeAccount/promotionalCode/index';

import { colors } from '../../../../styles/variables';
import { LoadingOutlined } from '@ant-design/icons';

import styles from './upgradeToProPayment.module.scss';

const PRICING_LINK = 'https://sightx.io/pricing';

interface IUpgradeToProPaymentProps {
  numberOfUsers: number;
  subscriptionCadence: CadenceTypesValues;
  backAction: () => void;
  nextAction: () => void;
}

interface IMapDispatchProps {
  resetPromotionalCodeState: () => void;
}

interface IMapStateProps {
  isConfirmingPayment: boolean;
  isUpgradingToPro: boolean;
  error: string | null;
  clientSecret: string | null;
  // Use for Feature.PromotionalCodes
  discount: IPromotionalCodeDiscount | undefined;
  prices: ISubscriptionPrices | undefined;
  // End of use for Feature.PromotionalCodes
}

interface ILocalState {
  clientSecret: string | null;
}

type Props = IUpgradeToProPaymentProps & IMapStateProps & IMapDispatchProps;

class UpgradeToProPaymentBase extends React.PureComponent<Props, ILocalState> {
  componentDidMount() {
    const { clientSecret } = this.props;
    this.setState({
      clientSecret
    });
    this.handleBackAction = this.handleBackAction.bind(this);
  }

  private handleBackAction() {
    if (isFeatureEnabled(Features.PromotionalCodes)) {
      this.props.resetPromotionalCodeState();
    }
    this.props.backAction();
  }

  private renderSupportingText() {
    const { discount, numberOfUsers, subscriptionCadence, prices } = this.props;
    const basePrice = isFeatureEnabled(Features.PromotionalCodes) ? getPriceByCadence(subscriptionCadence, prices) : pricePerCadence[subscriptionCadence];
    const amount = basePrice * numberOfUsers;
    const price = isFeatureEnabled(Features.PromotionalCodes) ? getDiscountedPrice(amount, discount) : amount;
    const text = 'You will be charged ';
    const timeFrame = subscriptionCadence === CadenceTypesValues.ANNUALLY ? 'year' : 'month';

    return (
      <FeatureToggle feature={Features.PromotionalCodes}>
        <On>
          <div>
            {text}
            <span className={styles.amount}>{`$${price}`} during the first {timeFrame}</span>
            . Auto renews at <a href={PRICING_LINK} target='blank' >regular rate</a>
          </div>
        </On>
        <Off>
          <span>{text}</span>&nbsp;
          <span className={styles.amount}>{`$${amount} ${subscriptionCadence.toLowerCase()}`}</span>
        </Off>
      </FeatureToggle>
    );
  }

  render(): React.ReactNode {
    const { clientSecret } = this.props;
    if (!clientSecret) {
      return <LoadingOutlined className={styles.paymentLoading} />;
    }

    const options: StripeElementsOptions = {
      clientSecret,
      appearance : {
        theme: 'none',
        variables: {
          fontFamily: 'Source Sans Pro, sans-serif',
          borderRadius: '6px',
          colorText: colors.baseBlack,
          colorDanger: colors.error500
        },
        rules: {
          '.Input--invalid': {
            border: `1px solid ${colors.error400}`
          },
          '.Input': {
            border: `1px solid ${colors.gray300}`,
            color: colors.gray900,
            fontSize: '14px',
            padding: '8px 12px'
          },
          '.Label': {
            fontSize: '14px'
          },
          '.Error': {
            fontSize: '12px',
            fontWeight: '400'
          }
        }
      }
    };

    return (
      <div className={styles.paymentsContainer}>
        <div className={cx(styles.subtitle, {
          [styles[Features.PromotionalCodes]]: isFeatureEnabled(Features.PromotionalCodes)
        })}>
          {this.renderSupportingText()}
        </div>
        <Elements stripe={stripePromise} options={options}>
          <CreditCardForm backAction={this.handleBackAction} nextAction={this.props.nextAction} />
        </Elements>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateProps => {
  const isConfirmingPayment = selectConfirmingPaymentProState(state);
  const isUpgradingToPro = selectUpgradingToProState(state);
  const error = selectUpgradeAccountError(state);
  const clientSecret = selectUpgradeToProClientSecret(state);
  const discountResult = getPromotionalDiscount(state);
  const pricesResult = selectSubscriptionPrices(state);
  const discount = discountResult !== BlockedByFeature ? discountResult : undefined;
  const prices = pricesResult !== BlockedByFeature ? pricesResult : undefined;

  return {
    isConfirmingPayment,
    isUpgradingToPro,
    error,
    clientSecret,
    discount,
    prices
  };
};

const mapDispatchToProps: IMapDispatchProps = {
  resetPromotionalCodeState: actions.resetState
};

export const UpgradeToProPayment = connect(mapStateToProps, mapDispatchToProps)(UpgradeToProPaymentBase);
